let joinPaths = (...paths: (string | undefined | null)[]) => {
  let p = paths.filter(Boolean).join('/').replace(/\/+/g, '/');
  return p.startsWith('/') ? p : `/${p}`;
};

export type InstanceParam = { id: string; slug: string } | null | undefined;

let InstancePaths = Object.assign(
  (instance: InstanceParam, ...subPages: (string | null | undefined)[]) => {
    if (!instance) return '#';

    return joinPaths('i', instance.slug, ...subPages);
  },
  {
    home: (instance: InstanceParam) => InstancePaths(instance),
    settings: (instance: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      InstancePaths(instance, 'settings', ...subPages),
    developer: (instance: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      InstancePaths(instance, 'developer', ...subPages)
  }
);

let WorkflowsPaths = Object.assign(
  (instance: InstanceParam, ...subPages: (string | null | undefined)[]) =>
    InstancePaths(instance, 'workflows', ...subPages),
  {
    agents: (instance: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      WorkflowsPaths(instance, 'agents', ...subPages),
    agent: (
      instance: InstanceParam,
      agent: InstanceParam,
      ...subPages: (string | null | undefined)[]
    ) => {
      if (!agent) return '#';

      return WorkflowsPaths(instance, 'agents', agent?.slug, ...subPages);
    },
    agentBuilder: (
      instance: InstanceParam,
      agent: { id: string; slug: string } | { id: string } | null | undefined
    ) => {
      if (!agent) return '#';
      return WorkflowsPaths(
        instance,
        'agents',
        'slug' in agent ? agent.slug : agent.id,
        'builder'
      );
    },

    agentTemplates: (instance: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      WorkflowsPaths(instance, 'agents/templates', ...subPages),
    agentConnections: (instance: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      WorkflowsPaths(instance, 'agents/connections', ...subPages),

    portals: (instance: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      WorkflowsPaths(instance, 'portals', ...subPages),
    portal: (
      instance: InstanceParam,
      portal: InstanceParam,
      ...subPages: (string | null | undefined)[]
    ) => {
      if (!portal) return '#';
      return WorkflowsPaths(instance, 'portals', portal.slug, ...subPages);
    },

    createAiConnection: (instance: InstanceParam) =>
      WorkflowsPaths(instance, 'connections/choose?type=ai'),
    createConnectionChoose: (
      instance: InstanceParam,
      params: {
        type: 'ai';
        next?: string | null | undefined;
        allowed?: string[];
      }
    ) => {
      let search = new URLSearchParams({ type: params.type });
      if (params.next) search.set('next', params.next);
      if (params.allowed?.length) search.set('allowed', params.allowed.join(','));

      return WorkflowsPaths(instance, `connections/choose?${search.toString()}`);
    },
    connection: (
      instance: InstanceParam,
      connection: { id: string },
      ...subPages: (string | null | undefined)[]
    ) => WorkflowsPaths(instance, 'connections', connection.id, ...subPages),
    setConnection: (instance: InstanceParam, i: { provider: string }) =>
      WorkflowsPaths(instance, `connections/set?provider=${i.provider}`),

    threads: (instance: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      WorkflowsPaths(instance, 'threads', ...subPages),
    thread: (
      instance: InstanceParam,
      thread: { id: string },
      ...subPages: (string | null | undefined)[]
    ) => WorkflowsPaths(instance, 'threads', thread.id, ...subPages),
    runs: (instance: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      WorkflowsPaths(instance, 'runs', ...subPages),
    run: (instance: InstanceParam, run: { id: string; threadId: string }) => {
      let thread = WorkflowsPaths.thread(instance, { id: run.threadId });

      return `${thread}?run_id=${run.id}`;
    },
    users: (instance: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      WorkflowsPaths(instance, 'users', ...subPages),
    user: (
      instance: InstanceParam,
      user: { id: string } | null | undefined,
      ...subPages: (string | null | undefined)[]
    ) => {
      if (!user) return '#';
      return WorkflowsPaths(instance, 'users', user.id, ...subPages);
    },
    groups: (instance: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      WorkflowsPaths(instance, 'groups', ...subPages),
    group: (
      instance: InstanceParam,
      group: { id: string } | null | undefined,
      ...subPages: (string | null | undefined)[]
    ) => {
      if (!group) return '#';
      return WorkflowsPaths(instance, 'groups', group.id, ...subPages);
    },
    statistics: (instance: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      WorkflowsPaths(instance, 'statistics', ...subPages)
  }
);

let AccountPaths = {
  settings: (...subPages: (string | null | undefined)[]) => joinPaths('account', ...subPages),
  emails: (...subPages: (string | null | undefined)[]) =>
    joinPaths('account', 'emails', ...subPages),
  security: (...subPages: (string | null | undefined)[]) =>
    joinPaths('account', 'security', ...subPages)
};

let OrganizationPaths = Object.assign(
  (organization: InstanceParam, ...subPages: (string | null | undefined)[]) => {
    if (!organization) return '#';

    return joinPaths('o', organization.slug, ...subPages);
  },
  {
    settings: (organization: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      OrganizationPaths(organization, ...subPages),
    billing: (organization: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      OrganizationPaths.settings(organization, 'billing', ...subPages),
    members: (organization: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      OrganizationPaths.settings(organization, 'members', ...subPages),
    invites: (organization: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      OrganizationPaths.settings(organization, 'invites', ...subPages),
    projects: (organization: InstanceParam, ...subPages: (string | null | undefined)[]) =>
      OrganizationPaths.settings(organization, 'projects', ...subPages)
  }
);

export let WelcomePaths = Object.assign(
  (...subPages: (string | null | undefined)[]) => joinPaths('welcome', ...subPages),
  {
    project: (i: { organizationId: string }) => {
      let inner = WelcomePaths('project');
      let search = new URLSearchParams({ organization_id: i.organizationId });
      return `${inner}?${search.toString()}`;
    },
    createProject: (
      i: { organizationId: string },
      ...subPages: (string | null | undefined)[]
    ) => {
      let inner = WelcomePaths('create-project');
      let search = new URLSearchParams({ organization_id: i.organizationId });
      return `${inner}?${search.toString()}`;
    }
  }
);

export let Paths = {
  join: joinPaths,

  instance: InstancePaths,
  workflows: WorkflowsPaths,
  account: AccountPaths,
  organization: OrganizationPaths,
  welcome: WelcomePaths
};
